import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Briefcase, Users, Sparkles } from 'lucide-react';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { Dataset } from '../../../types';

interface RecentDatasetsSectionProps {
  datasets: Dataset[] | null;
  onNewDataset: () => void;
}

const TableTypeIcon: React.FC<{ type?: string }> = ({ type }) => {
  switch (type) {
    case 'people':
      return <Users className="w-3 h-3 text-neutral-500" />;
    case 'custom':
      return <Sparkles className="w-3 h-3 text-neutral-500" />;
    default:
      return <Briefcase className="w-3 h-3 text-neutral-500" />;
  }
};

const getStatusStyle = (status?: string): string => {
  switch (status) {
    case 'completed':
      return 'text-green-600';
    case 'generating':
      return 'text-yellow-600';
    case 'completed_no_results':
      return 'text-neutral-400';
    default:
      return status?.startsWith('error') ? 'text-red-500' : 'text-neutral-400';
  }
};

const getStatusLabel = (status?: string): string => {
  switch (status) {
    case 'completed':
      return 'Complete';
    case 'generating':
      return 'Running';
    case 'completed_no_results':
      return 'No results';
    default:
      return status?.startsWith('error') ? 'Error' : 'Processing';
  }
};

export const RecentDatasetsSection: React.FC<RecentDatasetsSectionProps> = ({
  datasets,
  onNewDataset,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (!datasets || datasets.length === 0) {
    return (
      <section className="mb-10 pt-2">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 sm:p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-neutral-100 rounded-xl flex items-center justify-center">
            <Search className="w-5 h-5 text-neutral-400" />
          </div>
          <p className="text-[14px] font-medium text-neutral-900 mb-1">No datasets yet</p>
          <p className="text-[13px] text-neutral-500">Use the search above to get started</p>
        </div>
      </section>
    );
  }

  const displayedDatasets = showAll ? datasets : datasets.slice(0, 4);

  return (
    <section className="mb-10 pt-2" data-tour="recent-datasets">
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[13px] font-medium text-neutral-400 uppercase tracking-wider">
            Recent Datasets
          </h2>
          {datasets.length > 4 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-[11px] text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              {showAll ? 'Show less' : `See all ${datasets.length}`}
            </button>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-4 px-3 -mx-3 pb-2 mb-1 border-b border-neutral-100">
          <span className="flex-1 text-[10px] font-medium text-neutral-400 uppercase tracking-wide">Name</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-14 text-right">Results</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-20 text-center">Status</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-16 text-right">Created</span>
        </div>

        <div className="space-y-0">
          {displayedDatasets.map((dataset) => (
            <Link
              key={dataset.id}
              to={`/datasets/${dataset.id}`}
              className="group flex items-center gap-3 sm:gap-4 py-2.5 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors"
            >
              <div className="flex-1 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-6 h-6 bg-neutral-100 rounded flex items-center justify-center flex-shrink-0">
                  <TableTypeIcon type={dataset.table_type} />
                </div>
                <span className="text-[13px] text-neutral-900 truncate">
                  {dataset.name}
                </span>
              </div>
              <span className="text-[13px] text-neutral-500 tabular-nums w-10 sm:w-14 text-right">
                {dataset.lead_count ?? 0}
              </span>
              <span className={`text-[11px] font-medium w-16 sm:w-20 text-center ${getStatusStyle(dataset.status)}`}>
                {getStatusLabel(dataset.status)}
              </span>
              <span className="hidden sm:block text-[12px] text-neutral-400 w-16 text-right">
                {formatRelativeTime(dataset.created_at)}
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-neutral-100">
          <button
            onClick={onNewDataset}
            className="flex items-center gap-2 text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New dataset</span>
          </button>
        </div>
      </div>
    </section>
  );
};
